/**
 * 온보딩 페이지
 *
 * 최초 가입자(companyId === null)에게 두 가지 옵션 제공:
 * A) 데모로 시작하기 → createTestCompany → 즉시 사용
 * B) 회사 찾기 → searchCompanies + requestJoinCompany → 관리자 승인 대기
 *
 * 상태 머신: "select" → "search" → "pending" → "success"
 *
 * 디자인: 토스 스타일 — 소프트 그라데이션 배경 + 클린 카드 UI
 */
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useAuth } from "@/hooks/useAuth";
import { setAuthToken } from "@/lib/apollo";
import {
  CREATE_TEST_COMPANY,
  SEARCH_COMPANIES,
  REQUEST_JOIN_COMPANY,
  type CreateTestCompanyData,
  type SearchCompaniesData,
  type SearchCompaniesInput,
  type RequestJoinCompanyData,
  type RequestJoinCompanyInput,
  type CompanySearchResult,
} from "@/lib/graphql/user";
import "@/styles/OnboardingPage.css";

type OnboardingStep = "select" | "search" | "pending" | "success";

// ============================================================================
// useCreateDemoCompany: 데모 회사 생성 공용 훅 (DRY)
// ============================================================================

function useCreateDemoCompany(onSuccess?: () => void) {
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createTestCompany] = useMutation<CreateTestCompanyData>(CREATE_TEST_COMPANY);

  const handleDemo = async () => {
    setError("");
    setIsCreating(true);
    try {
      const { data } = await createTestCompany();
      if (data?.createTestCompany?.token) {
        setAuthToken(data.createTestCompany.token);
        onSuccess?.();
        setTimeout(() => window.location.reload(), 1200);
      } else {
        setError("데모 회사 생성에 실패했습니다. 다시 시도해주세요.");
        setIsCreating(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "알 수 없는 오류가 발생했습니다.";
      setError(message);
      setIsCreating(false);
    }
  };

  return { error, isCreating, handleDemo, clearError: () => setError("") };
}

export function OnboardingPage() {
  const { user } = useAuth();
  const [step, setStep] = useState<OnboardingStep>("select");
  const [pendingCompanyName, setPendingCompanyName] = useState("");

  // 이미 회사 소속이면 프로젝트 목록으로 이동
  if (user?.companyId) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div className="onboarding-page safe-area-top safe-area-bottom">
      <div className="ob-bg relative flex h-full flex-col overflow-hidden">
        {/* 배경 그라데이션 오브 */}
        <div className="ob-orb ob-orb-1" />
        <div className="ob-orb ob-orb-2" />
        <div className="ob-orb ob-orb-3" />

        {/* 메인 콘텐츠 */}
        <div className="relative z-10 flex flex-1 flex-col">
          {step === "select" && (
            <SelectStep onSearch={() => setStep("search")} onSuccess={() => setStep("success")} />
          )}
          {step === "search" && (
            <SearchStep
              onBack={() => setStep("select")}
              onPending={(companyName) => {
                setPendingCompanyName(companyName);
                setStep("pending");
              }}
            />
          )}
          {step === "pending" && (
            <PendingStep
              companyName={pendingCompanyName}
              onSearchAgain={() => setStep("search")}
              onSuccess={() => setStep("success")}
            />
          )}
          {step === "success" && <SuccessStep />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SelectStep: 소속 회사 선택
// ============================================================================

function SelectStep({ onSearch, onSuccess }: { onSearch: () => void; onSuccess: () => void }) {
  const { error, isCreating, handleDemo } = useCreateDemoCompany(onSuccess);

  return (
    <div className="flex flex-1 flex-col justify-center px-6">
      {/* 타이틀 */}
      <div className="mb-8 ob-anim-1">
        <h1
          className="text-[28px] font-bold leading-tight tracking-tight"
          style={{ color: "var(--color-primary)", letterSpacing: "-0.02em" }}
        >
          소속 회사를
          <br />
          선택해 주세요
        </h1>
        <p className="mt-3 text-[15px]" style={{ color: "var(--color-secondary)" }}>
          데모로 먼저 둘러보거나, 소속 회사에 가입할 수 있어요
        </p>
      </div>

      {/* 카드 */}
      <div className="w-full max-w-[400px] space-y-3">
        {/* 데모 카드 */}
        <button
          type="button"
          className="ob-card w-full text-left ob-anim-2"
          onClick={handleDemo}
          disabled={isCreating}
        >
          <div className="flex items-center gap-4">
            <div className="ob-icon ob-icon-demo">{isCreating ? <Spinner /> : <FlaskIcon />}</div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[16px] font-semibold"
                style={{ color: "var(--color-primary)", letterSpacing: "-0.01em" }}
              >
                {isCreating ? "생성 중..." : "데모로 시작하기"}
              </div>
              <div className="mt-1 text-[13px]" style={{ color: "var(--color-secondary)" }}>
                체험용 환경에서 바로 시작합니다
              </div>
            </div>
            <div className="ob-arrow">
              <ChevronRightIcon />
            </div>
          </div>
        </button>

        {/* 회사 찾기 카드 */}
        <button
          type="button"
          className="ob-card w-full text-left ob-anim-3"
          onClick={onSearch}
          disabled={isCreating}
        >
          <div className="flex items-center gap-4">
            <div className="ob-icon ob-icon-company">
              <BuildingIcon />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[16px] font-semibold"
                style={{ color: "var(--color-primary)", letterSpacing: "-0.01em" }}
              >
                회사 찾기
              </div>
              <div className="mt-1 text-[13px]" style={{ color: "var(--color-secondary)" }}>
                소속 회사를 검색하여 가입합니다
              </div>
            </div>
            <div className="ob-arrow">
              <ChevronRightIcon />
            </div>
          </div>
        </button>
      </div>

      {/* 에러 */}
      {error && <ErrorBox message={error} />}
    </div>
  );
}

// ============================================================================
// SearchStep: 회사 검색
// ============================================================================

function SearchStep({
  onBack,
  onPending,
}: {
  onBack: () => void;
  onPending: (companyName: string) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [error, setError] = useState("");
  const [joiningId, setJoiningId] = useState<string | null>(null);

  const [searchCompanies, { data: searchData, loading: searching }] = useLazyQuery<
    SearchCompaniesData,
    { input: SearchCompaniesInput }
  >(SEARCH_COMPANIES, { fetchPolicy: "network-only" });

  const [requestJoin] = useMutation<RequestJoinCompanyData, { input: RequestJoinCompanyInput }>(
    REQUEST_JOIN_COMPANY
  );

  const handleSearch = () => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setError("");
    searchCompanies({ variables: { input: { keyword: trimmed, limit: 10 } } });
  };

  const handleJoin = async (company: CompanySearchResult) => {
    setError("");
    setJoiningId(company._id);
    try {
      await requestJoin({ variables: { input: { companyId: company._id } } });
      onPending(company.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "가입 신청에 실패했습니다.");
    } finally {
      setJoiningId(null);
    }
  };

  const results = searchData?.searchCompanies ?? [];

  return (
    <div className="flex flex-1 flex-col ob-anim-in">
      {/* 헤더 */}
      <div className="px-6 pt-4 pb-2">
        <button type="button" onClick={onBack} className="ob-back" aria-label="뒤로가기">
          <ChevronLeftIcon />
          <span>뒤로</span>
        </button>
      </div>

      {/* 검색 영역 */}
      <div className="px-6 pb-5">
        <h2
          className="mb-1 text-[22px] font-bold"
          style={{ color: "var(--color-primary)", letterSpacing: "-0.02em" }}
        >
          회사 검색
        </h2>
        <p className="mb-5 text-[14px]" style={{ color: "var(--color-secondary)" }}>
          사업자번호 또는 회사명으로 검색하세요
        </p>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="relative flex-1">
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="사업자번호 또는 회사명"
              className="ob-search-input"
              autoFocus
            />
          </div>
          <button type="submit" disabled={!keyword.trim() || searching} className="ob-btn">
            {searching ? <Spinner color="white" /> : "검색"}
          </button>
        </form>
      </div>

      {/* 에러 */}
      {error && (
        <div className="px-6">
          <ErrorBox message={error} />
        </div>
      )}

      {/* 검색 결과 */}
      <div className="flex-1 overflow-auto px-6 py-3">
        {searching && (
          <div className="flex items-center justify-center py-12">
            <Spinner color="var(--color-accent)" />
            <span className="ml-2 text-[14px]" style={{ color: "var(--color-secondary)" }}>
              검색 중...
            </span>
          </div>
        )}

        {!searching && searchData && results.length === 0 && (
          <div
            className="py-12 text-center text-[14px]"
            style={{ color: "var(--color-secondary)" }}
          >
            검색 결과가 없습니다
          </div>
        )}

        {!searching && results.length > 0 && (
          <div className="space-y-3">
            {results.map((company) => (
              <div key={company._id} className="ob-result">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[15px] font-semibold"
                      style={{ color: "var(--color-primary)" }}
                    >
                      {company.name}
                    </div>
                    {company.businessNumber && (
                      <div className="mt-1 text-[13px]" style={{ color: "var(--color-secondary)" }}>
                        {company.businessNumber}
                      </div>
                    )}
                    <div className="mt-0.5 text-[12px]" style={{ color: "var(--color-tertiary)" }}>
                      {company.ceoName && `${company.ceoName} · `}
                      {company.memberCount}명
                    </div>
                  </div>
                  {company.canRequest ? (
                    <button
                      type="button"
                      className="ob-btn"
                      disabled={joiningId !== null}
                      onClick={() => handleJoin(company)}
                    >
                      {joiningId === company._id ? <Spinner color="white" /> : "가입 신청"}
                    </button>
                  ) : (
                    <span
                      className="shrink-0 rounded-lg px-3 py-1.5 text-[12px] font-medium"
                      style={{
                        background: "var(--color-surface)",
                        color: "var(--color-secondary)",
                      }}
                    >
                      신청 완료
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PendingStep: 승인 대기
// ============================================================================

function PendingStep({
  companyName,
  onSearchAgain,
  onSuccess,
}: {
  companyName: string;
  onSearchAgain: () => void;
  onSuccess: () => void;
}) {
  const { error, isCreating, handleDemo } = useCreateDemoCompany(onSuccess);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 ob-anim-in">
      {/* 대기 아이콘 */}
      <div className="ob-pending-ring mb-8">
        <div className="ob-pending-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="var(--color-amber)" strokeWidth="1.5" />
            <path
              d="M12 6v6l4 2"
              stroke="var(--color-amber)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {/* 타이틀 */}
      <h2
        className="text-[22px] font-bold"
        style={{ color: "var(--color-primary)", letterSpacing: "-0.02em" }}
      >
        가입 신청 완료
      </h2>

      {/* 회사명 뱃지 */}
      <div
        className="mt-3 rounded-xl px-4 py-2 text-[14px] font-semibold"
        style={{ background: "var(--color-accent-dim)", color: "var(--color-accent)" }}
      >
        {companyName}
      </div>

      <p
        className="mt-4 text-center text-[14px] leading-relaxed"
        style={{ color: "var(--color-secondary)" }}
      >
        관리자가 승인하면 자동으로
        <br />
        작업 환경이 설정됩니다
      </p>

      {/* 에러 */}
      {error && (
        <div className="mt-4 w-full max-w-[300px]">
          <ErrorBox message={error} />
        </div>
      )}

      {/* 액션 */}
      <div className="mt-10 flex w-full max-w-[300px] flex-col gap-3">
        <button
          type="button"
          className="ob-btn-dark w-full"
          onClick={handleDemo}
          disabled={isCreating}
        >
          {isCreating ? "생성 중..." : "우선 데모로 시작하기"}
        </button>

        <button
          type="button"
          className="ob-btn-ghost w-full"
          onClick={onSearchAgain}
          disabled={isCreating}
        >
          다른 회사 찾기
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SuccessStep: 완료
// ============================================================================

function SuccessStep() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6">
      <div
        className="ob-success-circle flex h-20 w-20 items-center justify-center rounded-full"
        style={{ background: "rgba(0, 196, 113, 0.08)" }}
      >
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path
            className="ob-success-check"
            d="M5 13l4 4L19 7"
            stroke="var(--color-success)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2
        className="mt-6 text-[20px] font-bold ob-anim-2"
        style={{ color: "var(--color-primary)", letterSpacing: "-0.02em" }}
      >
        설정 완료
      </h2>
      <p className="mt-2 text-[14px] ob-anim-3" style={{ color: "var(--color-secondary)" }}>
        잠시 후 이동합니다...
      </p>
    </div>
  );
}

// ============================================================================
// 인라인 아이콘
// ============================================================================

function FlaskIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 3h6M10 3v6.5L4 18a1 1 0 00.87 1.5h14.26A1 1 0 0020 18l-6-8.5V3" />
      <path d="M8.5 14h7" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="2" width="16" height="20" rx="1" />
      <path d="M9 22V12h6v10M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-tertiary)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function Spinner({ color = "var(--color-accent)" }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="animate-spin">
      <circle cx="12" cy="12" r="10" stroke="var(--color-border)" strokeWidth="2.5" />
      <path d="M12 2a10 10 0 019.95 9" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      className="mt-4 w-full max-w-[400px] rounded-2xl px-5 py-4 text-[14px] font-medium"
      style={{ background: "#fff0f0", color: "var(--color-error)" }}
    >
      {message}
    </div>
  );
}
